import { useState, useEffect, useCallback, useContext } from 'react'
import { useMutation, useLazyQuery } from "@apollo/client";
import { useParams, useNavigate, useLocation } from "react-router";
import { useDisclosure, useToast } from "@chakra-ui/react";
import { MONTHLY, YEARLY, USD, SUBSCRIPTION, ONETIMEPURCHASE } from "../../constants";
import { uploadImage } from "../../utils/helper";
import { pk_12 } from "../../config";
import {
    UPDATE_PRODUCT,
    CREATE_PRODUCT_SUBSCRIPTION_PLAN,
    UPDATE_PRODUCT_SUBSCRIPTION_PLAN,
    UPDATE_PRODUCT_ONE_TIME_PLAN,
    CREATE_PRODUCT_ONE_TIME_PLAN,
} from "../../graphql/mutations";
import {
    CATALOG,
    CATEGORIES_GRADES_SUBJECTS,
    PRODUCTDETAILS,
    PUBLISHERCATALOGS,
    SUB_CATEGORIES
} from "../../graphql/queries";
import { useBreadcrumbs } from "../../contexts/BreadcrumbProvider"
import { PublisherAndUserContext } from '../../contexts/PublisherAndUserContext';

export const useUpdateBook = () => {
    const { user, publisher } = useContext(PublisherAndUserContext);

    const { addMultiBreadCrumb } = useBreadcrumbs();
    const [dynoCategories, setDynoCategories] = useState([]);
    const [dynoSubCategories, setDynoSubCategories] = useState([]);
    const [buttonText, setButtonText] = useState("Create");
    const [catalogId, setCatalogId] = useState("");
    const [catalogs, setCatalogs] = useState("");
    const [productId, setProductId] = useState("");
    const [staff, setStaff] = useState([]);
    const [productAdmins, setProductAdmins] = useState([]);
    const [dynoGrades, setDynoGrades] = useState([]);
    const [dynoSubjects, setDynoSubjects] = useState([]);
    const [productGrades, setProductGrades] = useState([]);
    const [productSubjects, setProductSubjects] = useState([]);
    const [coverFile, setCoverFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [product, setProduct] = useState({
        productName: "",
        productType: "Book",
        productHeading: "",
        productDescription: "",
        isAddedToStore: false,
        productPricingType: null,
        productAdmins: [],
        productCategory: "",
        bookPublisher: {},
    });
    const [productSubscriptions, setProductSubscriptions] = useState([]);
    const [oneTimePlans, setOneTimePlans] = useState([]);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [plan, setPlan] = useState({
        planName: "",
        price: "",
        currency: USD,
        frequency: MONTHLY,
        planShortDescription: "",
        planLongDescription: "",
    });
    const [frequencyOptions] = useState([
        {
            id: "1",
            name: "Monthly",
            value: MONTHLY,
        },
        {
            id: "2",
            name: "Yearly",
            value: YEARLY,
        },
    ]);

    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: createCatalogIsOpen, onOpen: createCatalogOnOpen, onClose: createCatalogOnClose } = useDisclosure();
    const { pid: product_Id, cid: catalog_Id } = useParams();
    let navigate = useNavigate();
    const location = useLocation();
    const toast = useToast();

    const [getProduct] = useLazyQuery(PRODUCTDETAILS, {
        onCompleted: (data) => {
            const product = {
                ...data.product,
                productPricingType: data?.product?.productPricingType,
                productCategory: data?.product.productCategory.parentCategory?.id ? data?.product.productCategory.parentCategory?.id : data?.product.productCategory.id,
                productSubCategory: data?.product.productCategory.name === pk_12 ? null : data?.product.productCategory.id
            };
            setProduct(product);
            setProductAdmins(product.productAdmins)
            setProductGrades(product.productGrades);
            setProductSubjects(product.productSubjects);
            setOneTimePlans(product.productOneTimePlans);
            if (data.product.productSubscriptionPlans.length > 0)
                setProductSubscriptions(data.product.productSubscriptionPlans);
            setCatalogId(catalog_Id)
            setLoading(false);
            getCatalog({ //Need this network call to keep the catalog admins in disabled state and book admin in enabled state
                variables: {
                    where: {
                        id: catalog_Id,
                    },
                },
            });
            if (data?.product.productCategory.name !== pk_12) {
                getSubCategory({
                    variables: {
                        where: {
                            parentDynoCategoryId: {
                                equals: data?.product.productCategory.parentCategory?.id
                            }
                        }
                    }
                })
            }
            const url = new URL(window.location.href);
            const params = new URLSearchParams(url.search);
            if (params.has("createplan")) onOpen()
        },
        onError: () => {
            setLoading(false);
            toast({
                position: "top-right",
                title: "Failed to fetch book details. Please refresh the page.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        },
    });

    const [getCatalogs] = useLazyQuery(PUBLISHERCATALOGS, {
        onCompleted: (data) => {
            if (data.publisher?.catalogs?.length > 0) {
                setCatalogId(catalog_Id)
                setCatalogs(
                    data.publisher?.catalogs?.map((catalog, cindex) => ({
                        ...catalog,
                        index:
                            typeof catalog.index === "object"
                                ? cindex + 1
                                : catalog.index === cindex + 2
                                    ? cindex + 1
                                    : catalog.index,
                    }))
                );
                getCatalog({
                    variables: {
                        where: {
                            id: catalog_Id,
                        },
                    },
                });
            }
            setLoading(false);
        },
        onError: () => {
            setLoading(false);
            toast({
                position: "top-right",
                title: "Failed to fetch catalog details. Please refresh the page.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        },
    });

    const resetPlanAndCloseModal = () => {
        setPlan({
            planName: "",
            price: "",
            currency: USD,
            frequency: MONTHLY,
            planShortDescription: "",
            planLongDescription: "",
        });
        onClose();
    };

    const handleChangeCallback = (e) => {
        setPlan({
            ...plan,
            [e.target.name]:
                e.target.name === "price" ? Number(e.target.value) : e.target.value,
        });
    };
    const [getCatalog] = useLazyQuery(CATALOG, {
        onCompleted: (data) => {
            const catalogAdminsList = data.catalog.catalogAdmins.map((user) => ({
                ...user,
                name: `${user.firstName} ${user.lastName}`,
                disabled: true,
            }));
            const productAdminsList = productAdmins.map((user) => ({
                ...user,
                name: `${user.firstName} ${user.lastName}`,
                disabled: false,
            }));
            setProductAdmins(
                getSelectedListWithDisabled(productAdminsList, catalogAdminsList) // This function will default select the catalog admin as course admin
            );
            setLoading(false);
        },
        onError: () => {
            setLoading(false);
        },
    });

    const getSelectedListWithDisabled = (productAdminList, catalogAdminsList) => {
        let admins = [...productAdminList, ...catalogAdminsList]

        const userMap = new Map();

        admins.forEach(user => {
            if (userMap.has(user.id)) {
                // Replace only if the existing one has disabled: false and the new one has disabled: true
                if (!userMap.get(user.id).disabled && user.disabled) {
                    userMap.set(user.id, user);
                }
            } else {
                userMap.set(user.id, user);
            }
        });
        
        return Array.from(userMap.values());
    };

    useEffect(() => {
        if (product_Id) {
            setProductId(product_Id);
            setButtonText("Update");
            setLoading(true);
            getProduct({
                variables: {
                    where: {
                        id: product_Id,
                    },
                },
            });
        }
    }, [product_Id])

    useEffect(() => {
        if (
            publisher &&
            publisher.id &&
            publisher.additionalAdmins &&
            publisher.staff
        ) {
            setStaff(
                [
                    ...publisher.additionalAdmins,
                    ...publisher.staff,
                    publisher.owner,
                ].map((user) => ({
                    ...user,
                    name: `${user.firstName} ${user.lastName}`,
                }))
            );
        }
        if (publisher && publisher.id) {
            getCatalogs({
                variables: { where: { id: publisher.id } },
            });
        }
    }, [
        publisher,
        catalog_Id,
        getProduct,
        getCatalogs,
        setLoading,
    ]);

    const [updateProduct] = useMutation(UPDATE_PRODUCT, {
        onCompleted: (data) => {
            setLoading(false);
            addMultiBreadCrumb([{ label: "Home", path: "/" }, { label: "Catalog", path: "/catalogs" }, { label: data.updateProduct?.catalog.catalogName, path: `/catalog/${data.updateProduct.catalogId}` }, { label: data.updateProduct.productName, path: "" }])
            navigate(`/content/${data.updateProduct.id}`);
            setShowErrorMessage(false)
        },
        onError: () => {
            setLoading(false);
            toast({
                position: "top-right",
                title: "Failed to update book. Please try again.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        },
    });

    const [createProductSubscription] = useMutation(
        CREATE_PRODUCT_SUBSCRIPTION_PLAN,
        {
            onCompleted: (data) => {
                setLoading(false);
                setProductSubscriptions([...data.edshopifyCreateSubscriptionPriceForProduct.productSubscriptionPlans]);
                toast({
                    position: "top-right",
                    title: "Successfully created subscription plan.",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
                const searchParams = new URLSearchParams(location.search);
                searchParams.delete("createplan"); // Remove the 'createplan' param
                navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
            },
            onError: () => {
                setLoading(false);
                toast({
                    position: "top-right",
                    title: "Failed to create subscription plan. Please try again.",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            },
        }
    );

    const [updateProductSubscription] = useMutation(
        UPDATE_PRODUCT_SUBSCRIPTION_PLAN,
        {
            onCompleted: (data) => {
                setLoading(false);
                let productSubscriptionsCopy = [...productSubscriptions]
                let index = productSubscriptionsCopy.findIndex(sub => sub.frequency === data.edshopifyUpdateSubscriptionPriceForProduct.frequency)
                productSubscriptionsCopy[index] = {
                    ...data.edshopifyUpdateSubscriptionPriceForProduct
                }
                setProductSubscriptions([...productSubscriptionsCopy]);
                toast({
                    position: "top-right",
                    title: "Successfully updated subscription plan.",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
            },
            onError: () => {
                setLoading(false);
                toast({
                    position: "top-right",
                    title: "Failed to update subscription plan. Please try again.",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            },
        }
    );

    const [createProductOneTimePlan] = useMutation(
        CREATE_PRODUCT_ONE_TIME_PLAN,
        {
            onCompleted: (data) => {
                setLoading(false);
                setOneTimePlans([...data.edshopifyCreateOneTimePriceForProduct.productOneTimePlans]);
                toast({
                    position: "top-right",
                    title: "Successfully created plan.",
                    status: "success",
                    duration: 2000,
                    isClosable: true,
                });
                const searchParams = new URLSearchParams(location.search);
                searchParams.delete("createplan"); // Remove the 'createplan' param
                navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
            },
            onError: (err) => {
                setLoading(false);
                toast({
                    position: "top-right",
                    title: "Failed to create one time plan. Please try again.",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            },
        }
    );

    const [updateProductOneTimePlan] = useMutation(
        UPDATE_PRODUCT_ONE_TIME_PLAN,
        {
            onCompleted: (data) => {
                setLoading(false);
                setOneTimePlans([{...data?.edshopifyUpdateOneTimePriceForProduct}]);
                toast({
                  position: "top-right",
                  title: "Successfully updated plan.",
                  status: "success",
                  duration: 2000,
                  isClosable: true,
                });
            },
            onError: (err) => {
                setLoading(false);
                toast({
                    position: "top-right",
                    title: "Failed to update one time plan. Please try again.",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            },
        }
    );

    const [getCategoriesGradesSubjects] = useLazyQuery(CATEGORIES_GRADES_SUBJECTS, 
        {
            onCompleted: (data) => {
                setDynoCategories([data.dynoCategories.find(dc => dc.name === "PK-12"), ...data.dynoCategories.filter(dc => dc.name !== "PK-12")])
                setDynoGrades(data.dynoGrades)
                setDynoSubjects(data.dynoSubjects)
                setProduct({...product, productCategory: [data.dynoCategories.find(dc => dc.name === "PK-12"), ...data.dynoCategories.filter(dc => dc.name !== "PK-12")][0].id})
            }
        }
    )

    const [getSubCategory, {loading: subCategoriesLoading}] = useLazyQuery(SUB_CATEGORIES,
        {
            onCompleted: (data) => {
                setDynoSubCategories(data.dynoCategories)
            }
        }
    )

    useEffect(() => {
        getCategoriesGradesSubjects()
    }, []);

    const setProductCategory = (value) => {
        setProduct({ ...product, productCategory: value });
        getSubCategory({
            variables: {
                where: {
                    parentDynoCategoryId: {
                        equals: value
                    }
                }
            }
        })
    };

    const setProductSubCategory = (value) => {
        setProduct({ ...product, productSubCategory: value });
    };

    const parsePseudoJSON = (pseudoJSON) => {
        // '{\n    name: "Helen Keller",\n    heading: "Helen Keller: An Inspirational Life",\n    description: "Helen Keller was an amazing woman who overcame incredible challenges. She was born on June 27, 1880, in Alabama, USA. When she was just 19 months old, she got very sick and lost her sight and hearing. This made it very hard for her to communicate. But with the help of her teacher, Anne Sullivan, Helen learned how to read, write, and even speak. She went on to graduate from college and became a famous author and activist, helping others with disabilities. Her story shows that with determination and the right support, we can overcome difficulties and achieve great things.",\n    grade: "5",\n    subject: "English"\n}'
        // Replace single-line keys with quoted keys
        const correctedJSON = pseudoJSON.replace(/(\w+):/g, '"$1":');
        return JSON.parse(correctedJSON);
    };


    const handleChange = (e, id, key) => {
        if (e.target.name === "addPlan") {
            if (publisher?.stripeAccountId) {
                onOpen()
            } else {
                setShowErrorMessage(true)
            }
        } else {
            setProduct({
                ...product,
                [e.target.name]: e.target.value,
            });
        }

        if (e.target.name === "updatePlan") {
            setPlan(product[key].find(p => p.id === id))
            onOpen()
        }
    };

    const addContent = async (e) => {
        e.preventDefault();
        if (productGrades.length === 0) {
            toast({
                position: "top-right",
                title: "Please select grade.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return true;
        }
        if (productSubjects.length === 0) {
            toast({
                position: "top-right",
                title: "Please select subject.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return true;
        }
        setLoading(true);
        let productCoverSrc = "";
        if (coverFile) {
            productCoverSrc = await uploadImage(coverFile, "images");
        }
        if (productId) {
            let connectAdmin = productAdmins.map((admin) => ({ id: admin.id }));
            let disconnectAdmin = [];
            for (let ad of product.productAdmins) {
                const index = productAdmins.findIndex((ba) => ba.id === ad.id);
                if (index === -1) {
                    disconnectAdmin.push({ id: ad.id });
                }
            }
            let connectGrades = productGrades.map((grade) => ({ id: grade.id }));
            let disconnectGrades = [];
            let connectSubjects = productSubjects.map((grade) => ({ id: grade.id }));
            let disconnectSubjects = [];
            for (let grade of product.productGrades) {
                const index = productGrades.findIndex((pg) => pg.id === grade.id);
                if (index === -1) {
                    disconnectGrades.push({ id: grade.id });
                }
            }
            for (let subject of product.productSubjects) {
                const index = productSubjects.findIndex((pg) => pg.id === subject.id);
                if (index === -1) {
                    disconnectSubjects.push({ id: subject.id });
                }
            }
            let data = {
                ...(product.productDescription
                    ? { productDescription: { set: product.productDescription } }
                    : {}),
                ...(product.productHeading
                    ? { productHeading: { set: product.productHeading } }
                    : {}),
                ...(product.productSubHeading
                    ? { productSubHeading: { set: product.productSubHeading } }
                    : {}),
                ...(product.productCoverPath
                    ? { productCoverPath: { set: product.productCoverPath } }
                    : {}),
                ...(product.productName
                    ? { productName: { set: product.productName } }
                    : {}),
                ...(product.productStatus
                    ? { productStatus: { set: product.productStatus } }
                    : {}),
                ...(product.productType
                    ? { productType: { set: product.productType } }
                    : {}),
                ...(catalogId !== catalog_Id
                    ? {
                        catalog: {
                            connect: {
                                id: catalogId,
                            },
                        },
                    }
                    : {}),
                isAddedToStore: { set: product.isAddedToStore },
                productAdmins: {
                    connect: connectAdmin,
                    disconnect: disconnectAdmin,
                },
                productPricingType: { set: product?.productPricingType },
                productGrades: {
                    connect: connectGrades.filter((productGrade) => productGrade.id),
                    disconnect: disconnectGrades.filter(
                        (productGrade) => productGrade.id
                    ),
                },
                productSubjects: {
                    connect: connectSubjects.filter(
                        (productSubject) => productSubject.id
                    ),
                    disconnect: disconnectSubjects.filter(
                        (productSubject) => productSubject.id
                    ),
                },
                productCategory: {
                    connect: {
                        id: product.productSubCategory,
                    },
                },
                ...(productCoverSrc ? { productCoverPath: { set: productCoverSrc } } : {}),
            };
            delete data.__typename;
            delete data.id;
            updateProduct({
                variables: {
                    where: {
                        id: productId,
                    },
                    data: data,
                },
            });
            return true;
        }
    };

    const checklistCallback = useCallback((type, list) => {
        if (type === "productGrades") setProductGrades(list);
        if (type === "productAdmins") setProductAdmins(list);
        if (type === "productSubjects") setProductSubjects(list);
    }, []);

    const createUpdateSubscriptionProductPlan = (plan) => {
        setLoading(true);
        if (plan.id) {
            updateProductSubscription({
                variables: {
                    where: {
                        productId: productId,
                        id: publisher.id,
                    },
                    data: {
                        planName: plan.planName,
                        planShortDescription: plan.planShortDescription,
                        price: plan.price,
                        currency: plan.currency,
                        frequency: plan.frequency,
                    },
                },
            });
        } else {
            createProductSubscription({
                variables: {
                    where: {
                        id: publisher.id,
                        productId: productId
                    },
                    data: {
                        productPricingType: SUBSCRIPTION,
                        planName: "Product Price",
                        planShortDescription: "Product Price",
                        price: plan.price,
                        currency: USD,
                        frequency: plan.frequency,
                    },
                },
            });
        }
    };

    const createUpdateOneTimeProductPlan = (plan) => {
        setLoading(true);
        if (plan.id) {
            updateProductOneTimePlan({
                variables: {
                    where: {
                        id: publisher.id,
                        productId: productId
                    },
                    data: {
                        planName: plan.planName,
                        planShortDescription: plan.planShortDescription,
                        planLongDescription: plan.planShortDescription,
                        price: plan.price,
                        currency: plan.currency,
                    },
                },
            });
        } else {
            createProductOneTimePlan({
                variables: {
                    where: {
                        id: publisher.id,
                        productId: product_Id,
                    },
                    data: {
                        productPricingType: ONETIMEPURCHASE,
                        planName: "Product Price",
                        planShortDescription: "Product Price",
                        planLongDescription: "Product Price",
                        price: plan.price,
                        currency: USD,
                    },
                },
            });
        }
    };

    return {
        publisher,
        product,
        addContent,
        handleChange,
        dynoGrades,
        dynoSubjects,
        productGrades,
        productSubjects,
        checklistCallback,
        catalogs,
        catalogId,
        staff,
        productAdmins,
        createCatalogOnOpen,
        plan,
        frequencyOptions,
        handleChangeCallback,
        resetPlanAndCloseModal,
        createUpdateOneTimeProductPlan,
        createUpdateSubscriptionProductPlan,
        createCatalogIsOpen,
        createCatalogOnClose,
        dynoCategories,
        buttonText,
        setCoverFile,
        setProductCategory,
        setCatalogId,
        isOpen,
        loading,
        showErrorMessage,
        product_Id,
        productSubscriptions,
        oneTimePlans,
        user,
        dynoSubCategories,
        setProductSubCategory,
        subCategoriesLoading
    }
}
